import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { buildVendorParam } from "../utils/vendorSlug";
import CrawlLink from "../components/CrawlLink";
import VendorDirectoryLinks from "../components/VendorDirectoryLinks";
import { shopPath } from "../utils/seoPaths";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllActiveVendors } from "../redux/promotedVendorsSlice";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import { COUNTRY_NAME } from "../config/market";
import { VendorOfferTag } from "../components/DealTag";
import VendorTagline from "../components/VendorTagline";

const AllVendorsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { allVendors, isFetchingAll } = useSelector((state) => state.promotedVendors);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    dispatch(fetchAllActiveVendors());
  }, [dispatch]);

  // The shop a card opens, shared by the tap handler and the card's links.
  const vendorPathOf = (vendor) =>
    `/shop/${buildVendorParam(vendor.name, vendor.owner || vendor._id)}`;

  const handleVendorClick = (vendor) => {
    history.push(vendorPathOf(vendor));
  };

  const categories = useMemo(() => {
    const types = (allVendors || []).map((v) => v.vendorType).filter(Boolean);
    return ["All", ...Array.from(new Set(types))];
  }, [allVendors]);

  const activeVendors = useMemo(() => {
    return (allVendors || []).filter((vendor) => {
      const matchesSearch =
        vendor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vendor.address?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        activeCategory === "All" || vendor.vendorType === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [allVendors, searchQuery, activeCategory]);

  const hasUsableVendorImage = (vendor) => {
    if (!vendor?._id || !vendor?.imageUrl) return false;
    return !imageErrors[vendor._id];
  };

  const handleVendorImageError = (vendorId) => {
    if (!vendorId) return;
    setImageErrors((prev) => (prev[vendorId] ? prev : { ...prev, [vendorId]: true }));
  };

  const getVendorListingCount = (vendor) => {
    if (typeof vendor?.productCount === "number") return vendor.productCount;
    if (Array.isArray(vendor?.products)) return vendor.products.length;
    return 0;
  };

  const getVendorSpecializationBrief = (vendor) => {
    const directBrief =
      (typeof vendor?.typeOfGoods === "string" && vendor.typeOfGoods.trim()) ||
      (typeof vendor?.promoCaption === "string" && vendor.promoCaption.trim());

    if (directBrief) return directBrief;

    const type = typeof vendor?.vendorType === "string" ? vendor.vendorType.trim() : "";
    if (!type) return "";
    return `${type} products and services`;
  };

  return (
    <>
      <Navbar />
      <PageContainer>
        {/* Hero Header */}
        <HeroSection>
          <HeroInner>
            <HeroEyebrow>MARKETPLACE DIRECTORY</HeroEyebrow>
            <HeroTitle>All Vendors</HeroTitle>
            <HeroSubtitle>
              Discover businesses across {COUNTRY_NAME} delivering quality products to your door
            </HeroSubtitle>
            <SearchWrapper>
              <SearchIconWrap>
                <SearchIcon style={{ fontSize: 20, color: "#999" }} />
              </SearchIconWrap>
              <SearchInput
                type="text"
                placeholder="Search vendors by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <ClearBtn onClick={() => setSearchQuery("")}>&times;</ClearBtn>
              )}
            </SearchWrapper>
          </HeroInner>
        </HeroSection>

        <ContentSection>
          {/* Category Filter Chips */}
          {!isFetchingAll && categories.length > 1 && (
            <FilterRow>
              <FilterLabel>Filter by:</FilterLabel>
              <ChipScroller>
                {categories.map((cat) => (
                  <Chip
                    key={cat}
                    $active={activeCategory === cat}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </Chip>
                ))}
              </ChipScroller>
            </FilterRow>
          )}

          {isFetchingAll ? (
            <LoadingContainer>
              <CircularProgress style={{ color: "#ff9800" }} size={44} />
              <LoadingText>Loading vendors...</LoadingText>
            </LoadingContainer>
          ) : activeVendors.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>�</EmptyStateIcon>
              <EmptyStateTitle>No Vendors Found</EmptyStateTitle>
              <EmptyStateText>
                {searchQuery || activeCategory !== "All"
                  ? "Try adjusting your search or filter"
                  : "No vendors available at the moment"}
              </EmptyStateText>
              {(searchQuery || activeCategory !== "All") && (
                <ResetBtn onClick={() => { setSearchQuery(""); setActiveCategory("All"); }}>
                  Clear filters
                </ResetBtn>
              )}
            </EmptyState>
          ) : (
            <>
              <ResultsMeta>
                <ResultsCount>
                  {activeVendors.length} vendor{activeVendors.length !== 1 ? "s" : ""}
                  {activeCategory !== "All" ? ` in ${activeCategory}` : ""}
                </ResultsCount>
              </ResultsMeta>
              <VendorsGrid>
                {activeVendors.map((vendor) => (
                  <VendorCard
                    key={vendor._id}
                    onClick={() => handleVendorClick(vendor)}
                  >
                    {(() => {
                      const showVendorImage = hasUsableVendorImage(vendor);
                      const listingCount = getVendorListingCount(vendor);
                      const specializationBrief = getVendorSpecializationBrief(vendor);
                      return (
                        <>
                    {/* Hero Banner */}
                    <CrawlLink
                      to={shopPath("retail", vendor) || vendorPathOf(vendor)}
                      onNavigate={() => handleVendorClick(vendor)}
                      style={{ display: "block" }}
                    >
                      <CardHero>
                        {showVendorImage ? (
                          <CardHeroImg
                            src={vendor.imageUrl}
                            alt={vendor.name}
                            onError={() => handleVendorImageError(vendor._id)}
                          />
                        ) : (
                          <CardHeroPlaceholder>
                            <CardHeroPlaceholderGlow />
                            <CardHeroPlaceholderSurface />
                            <CardHeroPlaceholderIconWrap>
                              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.84)" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-6 9 6v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                                <path d="M9 21v-7h6v7"/>
                                <path d="M7.5 10.5h9"/>
                                <path d="M8.5 13.5h2.5"/>
                                <path d="M13 13.5h2.5"/>
                              </svg>
                            </CardHeroPlaceholderIconWrap>
                          </CardHeroPlaceholder>
                        )}
                        <CardHeroOverlay />
                        {vendor.vendorType && (
                          <CardCategoryPill>{vendor.vendorType}</CardCategoryPill>
                        )}
                        {/* Top-right: the category pill owns the top-left. */}
                        <VendorOfferTag
                          offer={vendor.offerSummary}
                          corner="right"
                          radius={16}
                          size="m"
                          zIndex={2}
                        />
                        {vendor.promoted && (
                          <CardFeaturedPill $belowDeal={Boolean(vendor.offerSummary)}>
                            ★ Featured
                          </CardFeaturedPill>
                        )}
                      </CardHero>
                    </CrawlLink>

                    {/* Card Body */}
                    <CardBody>
                      <CardAvatarRow>
                        {showVendorImage ? (
                          <CardAvatar
                            src={vendor.imageUrl}
                            alt={vendor.name}
                            onError={() => handleVendorImageError(vendor._id)}
                          />
                        ) : (
                          <CardAvatarPlaceholder>
                            {(vendor.name || "V").charAt(0).toUpperCase()}
                          </CardAvatarPlaceholder>
                        )}
                        <CardVerifiedDot title="Verified Partner">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </CardVerifiedDot>
                      </CardAvatarRow>

                      <CardVendorName>
                        <CrawlLink
                          to={shopPath("retail", vendor) || vendorPathOf(vendor)}
                          onNavigate={() => handleVendorClick(vendor)}
                        >
                          {vendor.name}
                        </CrawlLink>
                      </CardVendorName>
                      <CardTagline vendor={vendor} />

                      <CardMetaRow>
                        <CardListingCount>
                          {listingCount} product listing{listingCount !== 1 ? "s" : ""}
                        </CardListingCount>
                      </CardMetaRow>

                      {specializationBrief && (
                        <CardSpecializationBrief title={specializationBrief}>
                          {specializationBrief}
                        </CardSpecializationBrief>
                      )}

                      {vendor.address && (
                        <CardAddress>
                          <LocationOnIcon style={{ fontSize: 14, color: "#ff9800", flexShrink: 0 }} />
                          <span>{vendor.address}</span>
                        </CardAddress>
                      )}

                      <CardCTA onClick={(e) => { e.stopPropagation(); handleVendorClick(vendor); }}>
                        Shop Now
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 6 }}>
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </CardCTA>
                    </CardBody>
                        </>
                      );
                    })()}
                  </VendorCard>
                ))}
              </VendorsGrid>
            </>
          )}
        </ContentSection>
        {/* Every retail storefront, including the ones the cards above leave out. */}
        <VendorDirectoryLinks vertical="retail" title="All shops on Lusuku" />
      </PageContainer>
      <Footer />
    </>
  );
};

export default AllVendorsPage;

// ── Styled Components ──────────────────────────────────────────────────────────

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f7f7f7;
  padding-top: var(--lusuku-nav-h, 130px);

  @media (max-width: 850px) {
    padding-top: var(--lusuku-nav-h, 130px);
  }
`;

const HeroSection = styled.div`
  background: #111;
  padding: 3.5rem 1.5rem 4rem;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 60% 50%, rgba(255,152,0,0.18) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 600px) {
    padding: 2.5rem 1rem 3rem;
  }
`;

const HeroInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const HeroEyebrow = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 3px;
  color: #ff9800;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

const HeroTitle = styled.h1`
  color: #ffffff;
  font-size: 3rem;
  font-weight: 800;
  margin: 0 0 0.75rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -1px;
  line-height: 1.1;

  @media (max-width: 600px) {
    font-size: 2.25rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255,255,255,0.6);
  font-size: 1rem;
  margin: 0 0 2rem;
  line-height: 1.6;
`;

const SearchWrapper = styled.div`
  position: relative;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  padding: 0 16px;
  box-sizing: border-box;
  width: 100%;
`;

const SearchIconWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 10px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.95rem;
  padding: 15px 0;
  background: transparent;
  color: #222;
  box-sizing: border-box;

  &::placeholder {
    color: #aaa;
  }
`;

const ClearBtn = styled.button`
  border: none;
  background: none;
  font-size: 1.3rem;
  color: #aaa;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  flex-shrink: 0;

  &:hover { color: #555; }
`;

const ContentSection = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;

  @media (max-width: 600px) {
    padding: 1.5rem 1rem 3rem;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 0.5rem;
  }
`;

const FilterLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ChipScroller = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Chip = styled.button`
  padding: 6px 16px;
  border-radius: 20px;
  border: 1.5px solid ${(p) => (p.$active ? "#ff9800" : "#e0e0e0")};
  background: ${(p) => (p.$active ? "#ff9800" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#555")};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    border-color: #ff9800;
    color: ${(p) => (p.$active ? "#fff" : "#e65100")};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;
  gap: 1rem;
`;

const LoadingText = styled.p`
  color: #888;
  font-size: 0.95rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 5rem 1rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h2`
  color: #222;
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const EmptyStateText = styled.p`
  color: #888;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
`;

const ResetBtn = styled.button`
  padding: 10px 24px;
  background: #111;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover { background: #ff9800; }
`;

const ResultsMeta = styled.div`
  margin-bottom: 1.5rem;
`;

const ResultsCount = styled.p`
  color: #888;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
`;

const VendorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.75rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const VendorCard = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  transition: transform 0.28s ease, box-shadow 0.28s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.13);
  }
`;

const CardHero = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  overflow: hidden;
  background: #eee;
`;

const CardHeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${VendorCard}:hover & {
    transform: scale(1.06);
  }
`;

const CardHeroPlaceholder = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 18% 22%, rgba(255,255,255,0.14) 0%, transparent 42%),
    radial-gradient(circle at 82% 14%, rgba(255,193,7,0.2) 0%, transparent 46%),
    linear-gradient(142deg, #17344f 0%, #1f4f6e 42%, #0f2d43 100%);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardHeroPlaceholderGlow = styled.div`
  position: absolute;
  width: 190px;
  height: 190px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.04) 62%, transparent 74%);
  filter: blur(1px);
`;

const CardHeroPlaceholderSurface = styled.div`
  position: absolute;
  inset: auto 0 0;
  height: 66px;
  background:
    linear-gradient(to top, rgba(0,0,0,0.22), rgba(0,0,0,0)),
    repeating-linear-gradient(
      -35deg,
      rgba(255,255,255,0.07) 0,
      rgba(255,255,255,0.07) 10px,
      rgba(255,255,255,0.02) 10px,
      rgba(255,255,255,0.02) 20px
    );
`;

const CardHeroPlaceholderIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 94px;
  height: 94px;
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(255,255,255,0.3), rgba(255,255,255,0.08));
  border: 1px solid rgba(255,255,255,0.32);
  box-shadow:
    0 16px 32px rgba(0,0,0,0.3),
    inset 0 1px 0 rgba(255,255,255,0.52),
    inset 0 -2px 10px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
`;

const CardAvatarPlaceholder = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 12px;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.14);
  background:
    radial-gradient(circle at 28% 24%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.12) 32%, transparent 58%),
    linear-gradient(145deg, #ffb54b 0%, #f7941d 48%, #de6e05 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.15rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 1px 2px rgba(0,0,0,0.28);
`;

const CardHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0) 35%, rgba(0,0,0,0.42) 100%);
`;

const CardCategoryPill = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255,255,255,0.94);
  color: #e65100;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
`;

const CardFeaturedPill = styled.span`
  position: absolute;
  top: ${(p) => (p.$belowDeal ? "64px" : "10px")};
  right: 10px;
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
`;

const CardBody = styled.div`
  padding: 0 16px 18px;
`;

const CardAvatarRow = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: -26px;
  margin-bottom: 10px;
  position: relative;
  z-index: 2;
`;

const CardAvatar = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 12px;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.14);
  background: #f0f0f0;
`;

const CardVerifiedDot = styled.div`
  width: 20px;
  height: 20px;
  background: #ff9800;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -8px;
  border: 2px solid #fff;
  flex-shrink: 0;
`;

/* The vendor's own tagline under its name. Nothing renders when the vendor
   has not written one (components/VendorTagline.jsx). */
const CardTagline = styled(VendorTagline)`
  margin: 2px 0 8px;
`;

const CardVendorName = styled.h3`
  margin: 0 0 6px;
  font-size: 1.08rem;
  font-weight: 700;
  color: #1a1a1a;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.2px;
  line-height: 1.3;
`;

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const CardListingCount = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 152, 0, 0.12);
  border: 1px solid rgba(255, 152, 0, 0.32);
  color: #9f4d00;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const CardSpecializationBrief = styled.p`
  margin: 0 0 10px;
  color: #5f5f5f;
  font-size: 0.8rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardAddress = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  color: #888;
  font-size: 0.82rem;
  margin-bottom: 14px;
  line-height: 1.4;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const CardCTA = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 0;
  background: #1a1a1a;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.22s ease;
  letter-spacing: 0.2px;

  &:hover {
    background: #ff9800;
    box-shadow: 0 4px 14px rgba(255,152,0,0.32);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;
